import { CircularProgress } from "@mui/material";
import React, { useContext } from "react";
import { UserContext } from "../../../../../../App";
import styled from "@emotion/styled";

const StyledButtonWrapper = styled.button`
  background-color: ${({ variant, color }) => (variant === "primary" ? color : "transparent")};
  border: ${({ variant, color }) =>
    variant === "primary" ? "1px solid transparent" : `1px solid ${color}`};
  color: ${({ variant, color }) => (variant === "primary" ? "white" : color)};
  cursor: ${({ disabled, loading }) => (disabled || loading ? "not-allowed" : "pointer")};
  opacity: ${({ disabled, loading }) => (disabled || loading ? "0.5" : "1")};
  height: ${({ size }) =>
    size === "small"
      ? "2rem"
      : size === "medium"
      ? "2.5rem"
      : size === "large"
      ? "3rem"
      : "2.8rem"};
  border-radius: 8px;
  text-transform: uppercase;
  gap: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  padding: 0.5rem 1rem;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  transition:
    all 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    transform: translateY(-3px);
  }
`;

/**
 * A custom button component that provides a flexible and customizable button that can be used in a variety of contexts.
 *
 * @param {Object} props - The props for the component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the button.
 * @param {string} [props.variant="primary"] - The button variant, which can be either "primary" or "secondary".
 * @param {string} [props.color="#2954A5"] - The color of the button. This can be any valid CSS color value.
 * @param {string} [props.size] - The size of the button, which can be either "small", "medium", "large", or undefined.
 * @param {boolean} [props.disabled=false] - A boolean value that determines whether the button is disabled.
 * @param {boolean} [props.loading=false] - A boolean value that determines whether the button is in a loading state.
 * @param {Object} [props.rest] - Any additional props to be passed to the button element.
 * @returns {React.ReactNode} The `StyledButton` component.
 */
const StyledButton = ({
  children,
  variant = "primary",
  color: propColor,
  size,
  fullWidth = false,
  disabled = false,
  loading = false,
  style,
  ...rest
}) => {
  const { color: contextColor } = useContext(UserContext);
  const buttonColor = contextColor === "dark" ? "var(--light-blue)" : "var(--blue)";
  const color = propColor || buttonColor; // use prop color if available, otherwise use button color

  return (
    <StyledButtonWrapper
      variant={variant}
      color={color}
      size={size}
      style={style}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <CircularProgress
          color="success"
          sx={{ color: variant === "primary" ? "white" : color, display: "flex" }}
          size={23}
        />
      ) : (
        children
      )}
    </StyledButtonWrapper>
  );
};

export default StyledButton;
